import { Button, Dialog, DialogContent, DialogTitle, Skeleton, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import { List, useNotify, useTranslate } from 'react-admin';
import { parseDateFormat } from 'src/helper/parseDateFormat';
import { reportingServices } from 'src/services/sales/reporting/reporting.service';
import {useReportData} from './logic/useReportData';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { ReportingForm } from './ReportingForm';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Delete from '@mui/icons-material/Delete';
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog';
import { CardCommonExpandible } from 'src/components/uiElements/cards/CardCommonExpandible';
import { ReportingContext } from './logic/ReportingContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import { HasPermission, hasPermissionFunction } from 'src/helper/HasPermission';

const ReportRow = ({field, category}) =>{
  const label = useReportData(field, category)
  return(
    <TableCell>{label ?? ''}</TableCell>
  )
}

export const ReportingList = () => {
    const translate = useTranslate()
    const notify = useNotify()
    const [reportUrls, setReportUrls] = useState([])
    const [reports, setReports] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isReportLoading, setIsReportLoading] = useState(false)
    const [openEditDialog, setOpenEditDialog] = useState(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
    const [reportToDelete, setReportToDelete] = useState(null)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [onConfirmDelete, setOnConfirmDelete] = useState(false)
    const [editReportId, setEditReportId] = useState(null)
    const [isShow, setIsShow] = useState(false)
    const [disableRefresh, setDisableRefresh] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [toggleRefresh, setToggleRefresh] = useState(false)
    const [paginationUrl, setPaginationUrl] = useState({ page: 0, rowsPerPage: 10 })
    const {activateRefresh, setActivateRefresh, refresh, setRefresh, setTypes, types} = useContext(ReportingContext);
    
    const getReportsTypes = async () =>{
      reportingServices.getTypes().then(res=>{
        setTypes(res?.filter(type=>hasPermissionFunction('reporting', type.permission))
          .map(type=>({...type, name:translate(`ra.message.${type.name}`)})
        ))
      })
    }
  
    useEffect(()=>{
      getReportsTypes()
    },[])

    useEffect(()=>{
      getReports()
    },[refresh])

    useEffect(()=>{
      if(activateRefresh){
        setTimeout(()=>getUrlReports(), 30000)
      }
    },[activateRefresh, toggleRefresh])

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        setRefresh(value=>!value)
      }
    });

    useEffect(()=>{
        getUrlReports()
    },[refresh, paginationUrl])

    const onPageItemChange = (number) => {
      const paginationItemDataAux = { ...paginationUrl }
      paginationItemDataAux.page = number
      setPaginationUrl(paginationItemDataAux)
    }

    useEffect(()=>{
      if(onConfirmDelete){
        setDeleteLoading(true)
        reportingServices.deleteReport(reportToDelete.id).then(async ()=>{
          setOpenDeleteDialog(false)
          setOnConfirmDelete(false)
          notify('ra.notification.report_deleted_success',{type:'success'})
          await getReports()
        }).catch(()=>{
          notify('ra.notification.report_deleted_error',{type:'error'})
        }).finally(()=>{
          setDeleteLoading(false)
        })
      }
    },[onConfirmDelete])

    const onRowsPerPageItemChange = (e) => {
      const paginationItemDataAux = { ...paginationUrl }
      paginationItemDataAux.rowsPerPage = parseInt(e.target.value, 10)
      paginationItemDataAux.page = 0
      setPaginationUrl(paginationItemDataAux)
    }

    const getReports = async () =>{
      setIsReportLoading(true)
      await reportingServices.getReports().then(res=>{
        setReports(res)
      }).finally(()=>{
        setIsReportLoading(false)
      })
    }

    const lowercaseFirstLetter = (str) => {
      return str.charAt(0).toLowerCase() + str.slice(1);
    }

    const getUrlReports = () =>{
        setIsLoading(true)
        reportingServices.getUrlReports({offset:paginationUrl.page, limit:paginationUrl.rowsPerPage}).then(res=>{
            setReportUrls(res.data)
            setTotalCount(res.total)
            if(activateRefresh){
              if(res.total !== totalCount){
                setActivateRefresh(false)
              } else {
                setToggleRefresh(!toggleRefresh)
              }
            }
        }).finally(()=>{
            setIsLoading(false)
        })
    }

    const onEdit = (id) =>{
      setEditReportId(id)
    }

    useEffect(()=>{
      if(editReportId){
        setOpenEditDialog(true)
      }
    },[editReportId])

    useEffect(()=>{
      if(!openEditDialog){
        setEditReportId(null)
      }
    },[openEditDialog])

  const downloadFile = (url) =>{
    const link = document.createElement('a');
    link.href = url;
    link.download = 'report_';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } 
  return (
    <div>
        <CardCommonExpandible defaultExpanded={true} title={translate('ra.title.scheduled_reports')}>
        <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{translate('ra.field.name')}</TableCell>
                  <TableCell>{translate('ra.field.type')}</TableCell>
                  <TableCell>{translate('ra.field.creator')}</TableCell>
                  <TableCell>{translate('ra.field.sending_frequency')}</TableCell>
                  <TableCell>{translate('ra.field.updated_at')}</TableCell>
                  <TableCell>{translate('ra.field.is_active')}</TableCell>
                  <TableCell>{translate('ra.title.actions')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isReportLoading ? (
                  <TableRow>
                    {[...Array(8)].map((_, i) => (
                      <TableCell key={i}>
                        <Skeleton variant='rectangular' width={'100%'} height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ) :
                (reports?.map((row, i) => (
                  types.find(type=>type.id === row.type) &&
                    <TableRow key={i}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{types.find(type=>type.id === row.type)?.name ?? ''}</TableCell>
                      <TableCell>{row.creator}</TableCell>
                      <ReportRow field={row.type_frequency} category={'sendingPeriods'} />
                      <TableCell>{parseDateFormat(row.updated_at)}</TableCell>
                      <TableCell>
                        {
                          row.is_active ?
                          <DoneIcon/> :
                          <CloseIcon/>
                        }
                      </TableCell>
                      <TableCell>
                        <div style={{display:'flex', flexDirection:'row', justifyContent:'space-between', width:'60%'}}> 
                          <HasPermission permission={'show'} resource={'reporting'}>
                            <VisibilityIcon fontSize='small' color='info' style={{cursor:'pointer'}} onClick = {()=>{onEdit(row.id); setIsShow(true)}}/>
                          </HasPermission>
                          <HasPermission permission={'edit'} resource={'reporting'}>
                            <EditIcon 
                              fontSize='small' 
                              color={row.permission === 'edit' ? 'info' : 'disabled'} 
                              onClick = {()=>{
                                if(row.permission === 'edit'){
                                  onEdit(row.id); 
                                  setIsShow(false)
                                }
                              }} 
                              style={{cursor:row.permission === 'edit' ?'pointer':'default'}}
                            />
                          </HasPermission>
                          <HasPermission permission={'delete'} resource={'reporting'}>
                            <Delete 
                              fontSize='small'
                              style={{cursor:row.permission === 'edit' ?'pointer':'default'}} 
                              onClick={()=>{
                                if(row.permission === 'edit'){
                                  setReportToDelete({id:row.id, name:row.name})
                                  setOpenDeleteDialog(true)
                                }
                              }} color={row.permission === 'edit' ? 'error' :'disabled'}
                            />
                          </HasPermission>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )
                }
              </TableBody>
              <TableFooter>

                </TableFooter>
            </Table>
          </TableContainer>
          </CardCommonExpandible>
          <br/>
        <CardCommonExpandible defaultExpanded={true}  title={translate('ra.title.generated_reports')}>
        <TableContainer>
            <Table>
              <TableHead>
                <TableRow >
                  <TableCell colSpan={4} align='right'>
                    {!isLoading && <RefreshIcon color={disableRefresh?'disabled':'black'} style={{cursor:disableRefresh ? 'disabled' :'pointer'}} 
                    onClick={()=>{
                      if(disableRefresh){
                        return
                      }
                      getUrlReports()
                      setDisableRefresh(true)
                      setTimeout(()=>setDisableRefresh(false), 20000)
                    }}
                    />}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ra.field.name')}</TableCell>
                  <TableCell>{translate('ra.field.config')}</TableCell>
                  <TableCell>{translate('ra.field.created_at')}</TableCell>
                  <TableCell>{translate('ra.title.file')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    {[...Array(8)].map((_, i) => (
                      <TableCell key={i}>
                        <Skeleton variant='rectangular' width={'100%'} height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ) :
                (reportUrls?.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.reporting_id ? translate('ra.message.scheduled') : translate('ra.message.on_demand')}</TableCell>
                      <TableCell>{parseDateFormat(row.created_at)}</TableCell>
                      <TableCell>
                        {(row.url && row.url.includes('https://')) ?
                          <Button onClick={()=>downloadFile(row.url)}>
                            {translate('ra.action.download')}
                          </Button>:
                          <label>
                            {row.url ? translate(`ra.title.${lowercaseFirstLetter(row.url)}`): ''}
                          </label>
                        }
                      </TableCell>
                    </TableRow>
                    ))
                )
                }
              </TableBody>
              <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      count={totalCount}
                      rowsPerPage={paginationUrl.rowsPerPage}
                      page={paginationUrl.page}
                      onPageChange={(e, number) => onPageItemChange(number)}
                      onRowsPerPageChange={(e) => onRowsPerPageItemChange(e)}
                      labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} ${translate('ra.navigation.of')} ${count}`
                      }
                    />
                  </TableRow>
                </TableFooter>
            </Table>
          </TableContainer>
          </CardCommonExpandible>
          <Dialog open={openEditDialog}  fullWidth maxWidth='lg' onClose={()=>{setOpenEditDialog(false)}}>
                  <DialogTitle style={{display:'flex', flexDirection:'row', width:'100%', justifyContent:'space-between'}}>{translate('ra.title.edit_report')}
                    <CloseIcon style={{cursor:'pointer'}} onClick={()=>{setOpenEditDialog(false)}}/>
                  </DialogTitle>
                  <DialogContent>
                      <ReportingForm isShow={isShow} setRefresh={setRefresh} reportId={editReportId} setOpenEditDialog={setOpenEditDialog}/>
                  </DialogContent>
          </Dialog>
          <ConfirmationDialog
            open={openDeleteDialog}
            setOpen={setOpenDeleteDialog}
            setConfirm={setOnConfirmDelete}
            loadingModalButton = {deleteLoading}
            buttonCancel={translate('ra.action.cancel')}
            buttonConfirmText={translate('ra.action.to_confirm')}
            contentText={translate('ra.message.confirm_delete_report',{name:reportToDelete?.name})}
          />
    </div>
  )
}
